import { motion } from "framer-motion";
import { ExternalLink, Package, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function MachineCard({ machine }) {
  // Check if image URL is valid
  const hasValidImageUrl = machine?.image_url && 
    typeof machine.image_url === 'string' && 
    machine.image_url.trim() !== '' &&
    (machine.image_url.startsWith('http://') || machine.image_url.startsWith('https://'));
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group"
      data-testid="machine-card"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Machine Image */}
        {hasValidImageUrl ? (
          <div className="w-full sm:w-48 h-48 flex-shrink-0 overflow-hidden bg-slate-100">
            <img 
              src={machine.image_url} 
              alt={machine.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
              }}
            />
            <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 items-center justify-center flex-shrink-0 hidden">
              <Package className="h-16 w-16 text-green-600" />
            </div>
          </div>
        ) : (
          <div className="w-full sm:w-48 h-48 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center flex-shrink-0">
            <Package className="h-16 w-16 text-green-600" />
          </div>
        )}
        
        {/* Machine Details */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-bold text-lg text-slate-900 mb-1" data-testid="machine-name">
                {machine.name}
              </h3>
              <div className="flex gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  <Package className="h-3 w-3" />
                  {machine.category}
                </span>
                {machine.brand && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                    <Building2 className="h-3 w-3" />
                    {machine.brand}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <p className="text-sm text-slate-600 mb-3" data-testid="machine-description">
            {machine.description}
          </p>
          
          {machine.recommendation_reason && (
            <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-3 rounded">
              <p className="text-sm text-green-800">
                <span className="font-semibold">¿Por qué esta máquina?</span> {machine.recommendation_reason}
              </p>
            </div>
          )}
          
          {machine.specs && machine.specs.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-slate-700 mb-1">Especificaciones:</p>
              <ul className="text-xs text-slate-600 space-y-1">
                {machine.specs.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    {spec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {machine.url && (
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-full text-green-700 border-green-300 hover:bg-green-50"
              onClick={() => window.open(machine.url, '_blank')}
              data-testid="machine-details-button"
            >
              Ver Detalles
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}